import { Copy, Download, FileJson, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { evidenceSources, scientificQuestionReferences } from "@/data/evidenceCatalog";
import { cn } from "@/lib/utils";

type Region = "中文产品" | "国外产品";
type IntakeMethod =
	| "单个问题"
	| "问卷/量表"
	| "场景选择"
	| "自由输入"
	| "AI对话"
	| "语音"
	| "面部情绪识别"
	| "多模态"
	| "生理数据"
	| "行为记录";
type AnswerFormat =
	| "单选"
	| "多选"
	| "打分"
	| "文本"
	| "量表"
	| "对话"
	| "语音"
	| "图片"
	| "自动采集";
type EvidenceLevel = "官方明确" | "标准工具" | "体验记录" | "推测";
type DailyFit = "每日必问" | "每周追踪" | "入门筛查" | "触发时询问" | "练后反馈" | "暂不适合";

type QuestionRecord = {
	id: string;
	product: string;
	region: Region;
	productType: string;
	tags: string;
	question: string;
	answerFormat: AnswerFormat;
	intakeMethod: IntakeMethod;
	dailyFit: DailyFit;
	questionDomain: string;
	testedState: string;
	detectedState: string;
	emotionTags: string;
	courseCategory: string;
	training: string;
	exerciseType: string;
	trainingTheory: string;
	dataPurpose: string;
	productCore: string;
	reason: string;
	evidenceLevel: EvidenceLevel;
	source: string;
	notes: string;
	createdAt: string;
};

type DraftRecord = Omit<QuestionRecord, "id" | "createdAt">;

const storageKey = "psychology.records.v5";

const intakeMethods: IntakeMethod[] = [
	"单个问题",
	"问卷/量表",
	"场景选择",
	"自由输入",
	"AI对话",
	"语音",
	"面部情绪识别",
	"多模态",
	"生理数据",
	"行为记录",
];

const evidenceLevels: EvidenceLevel[] = ["官方明确", "标准工具", "体验记录", "推测"];
const regions: Region[] = ["中文产品", "国外产品"];

const productTypes = [
	"标准量表",
	"治疗框架",
	"积极心理学框架",
	"临床指南/干预框架",
	"AI情绪陪伴",
	"AI 情绪陪伴 + 轻训练 + 成长计划",
	"正念冥想",
	"睡眠工具",
	"情绪日记",
	"咨询平台",
	"习惯训练",
	"积极心理学训练",
	"自我照顾 + 微习惯打卡 + 宠物养成",
	"心情日记 + 习惯追踪",
	"情绪识别",
	"心理测评/课程",
	"心理测评平台",
	"压力管理 + 冥想课程",
];

const questionDomains = [
	"情绪状态",
	"身体能量",
	"睡眠压力",
	"关系场景",
	"行动动力",
	"价值目标",
	"安全风险",
	"练后反馈",
];

const emptyDraft: DraftRecord = {
	product: "",
	region: "中文产品",
	productType: "",
	tags: "",
	question: "",
	answerFormat: "单选",
	intakeMethod: "单个问题",
	dailyFit: "每日必问",
	questionDomain: "",
	testedState: "",
	detectedState: "",
	emotionTags: "",
	courseCategory: "",
	training: "",
	exerciseType: "",
	trainingTheory: "",
	dataPurpose: "",
	productCore: "",
	reason: "",
	evidenceLevel: "体验记录",
	source: "",
	notes: "",
};

type SeedRecord = Pick<
	QuestionRecord,
	"id" | "product" | "productType" | "question" | "questionDomain" | "emotionTags" | "productCore"
> &
	Partial<
		Omit<
			QuestionRecord,
			| "id"
			| "product"
			| "productType"
			| "question"
			| "questionDomain"
			| "emotionTags"
			| "productCore"
			| "createdAt"
		>
	>;

const sourceDate = "2026-06-12";
const finchProductCore = "用微小目标 + 宠物奖励，把自我照顾转成可持续打卡";
const finchTrainingTheory = "行为激活, 微习惯, 正念, 积极心理, 奖励强化";

const createRecord = ({
	id,
	product,
	region = "国外产品",
	productType,
	tags = "",
	question,
	answerFormat = "单选",
	intakeMethod = "单个问题",
	dailyFit = "触发时询问",
	questionDomain,
	testedState = "",
	detectedState = "",
	emotionTags,
	courseCategory = "",
	training = "",
	exerciseType = "",
	trainingTheory = "",
	dataPurpose = "",
	productCore,
	reason = "",
	evidenceLevel = "体验记录",
	source = "Untitled.md",
	notes = "",
}: SeedRecord): QuestionRecord => ({
	id,
	product,
	region,
	productType,
	tags,
	question,
	answerFormat,
	intakeMethod,
	dailyFit,
	questionDomain,
	testedState,
	detectedState,
	emotionTags,
	courseCategory,
	training,
	exerciseType,
	trainingTheory,
	dataPurpose,
	productCore,
	reason,
	evidenceLevel,
	source,
	notes,
	createdAt: sourceDate,
});

const domainToQuestionDomain: Record<string, string> = {
	action: "行动动力",
	body: "身体能量",
	order: "行动动力",
	positive: "价值目标",
	relationship: "关系场景",
	safety: "安全风险",
	self: "情绪状态",
	sleep: "睡眠压力",
	stress: "情绪状态",
};

const scientificReferenceRecords: QuestionRecord[] = scientificQuestionReferences.map(
	(reference) => {
		const source = evidenceSources[reference.sourceId];
		const questionDomain = domainToQuestionDomain[reference.relatedDomainIds[0] ?? "stress"];

		return createRecord({
			id: reference.id,
			product: source.title,
			region: "国外产品",
			productType: source.type,
			tags: `科学参考, ${source.title}, ${source.type}, ${reference.title}`,
			question: reference.questionStem,
			answerFormat: reference.answerFormat,
			intakeMethod: "问卷/量表",
			dailyFit: "入门筛查",
			questionDomain,
			testedState: reference.testedState,
			detectedState: reference.detectedState,
			emotionTags: reference.emotionTags,
			courseCategory: reference.courseCategory,
			training: reference.productizedUse,
			exerciseType: reference.exerciseType,
			trainingTheory: reference.trainingTheory,
			dataPurpose: reference.productizedUse,
			productCore: "作为标准量表/成熟干预框架的参考源，支撑题目改写、状态识别和练习推荐。",
			reason: "科学参考价值高于普通竞品观察；前台题目应从该维度改写，并保留评分口径和使用边界。",
			evidenceLevel: "标准工具",
			source: `${source.title}｜${source.url}`,
			notes: `参考维度：${reference.title}。时间窗口：${reference.timeWindow}。评分口径：${reference.scoringNote}。使用边界：${reference.caution}`,
		});
	},
);

const finchGoalCategories = [
	{
		key: "easy-wins",
		category: "Easy Wins",
		zhName: "轻松小胜",
		questionDomain: "行动动力",
		core: "超级简单、快速完成的任务，提升动力和成就感",
		examples: "起床、喝水、整理床铺、刷牙、打开窗户、深呼吸 3 次",
		detectedState: "低动力、拖延、无力，需要启动",
		emotionTags: "低落无力, 拖延失控",
		courseCategory: "行动启动",
		exerciseType: "行动启动",
		goals: [
			"字面意义上活过这一天",
			"单纯地存在就好",
			"喝水",
			"喝热水",
			"起床",
			"换衣服",
			"深呼吸 3 次",
			"刷牙",
			"洗脸",
			"看着镜子说“我可以的”",
			"站起来 10 秒",
			"说出一个关心我的人",
			"出门一次",
			"洗澡",
		],
	},
	{
		key: "calm",
		category: "Calm",
		zhName: "平静 / 放松",
		questionDomain: "情绪状态",
		core: "减压、放松神经、正念练习",
		examples: "正念呼吸、冥想、听白噪音、伸展放松、喝茶",
		detectedState: "焦虑、压力、紧张、烦躁、睡前过载",
		emotionTags: "焦虑压力, 烦躁愤怒",
		courseCategory: "平静放松",
		exerciseType: "呼吸放松",
		goals: [
			"深呼吸 3 次",
			"做一次正念呼吸练习",
			"避免在床上刷灾难性内容",
			"好好享用午餐，不看屏幕",
			"说出我现在的心情",
			"睡前说出一件感恩的事",
			"把所有想法和担忧写下来",
			"喝水休息一下",
			"睡前做睡眠呼吸练习放松",
			"睡前阅读放松的内容",
			"晚饭后泡一杯洋甘菊茶",
			"关闭通知 1 小时",
			"关闭没用的浏览器标签页",
			"聆听环境音 / 白噪音",
			"下午 6 点前停止工作",
			"避免无意识刷社交媒体",
			"想象一个宁静的地方",
			"点一支蜡烛",
			"快速身体扫描（检查紧张或放松）",
			"用舒适的毯子裹住自己",
			"在大自然中散步",
			"专注地短距离散步",
			"每吃一口就放下叉子",
			"吃饭时不看手机或电视",
		],
	},
	{
		key: "connection",
		category: "Connection",
		zhName: "连接 / 社交",
		questionDomain: "关系场景",
		core: "人际关系、与他人/自己连接",
		examples: "给朋友发消息、拥抱家人、写感谢卡、和 birb 聊天、社交媒体积极互动",
		detectedState: "孤独、断联、关系疏离，需要连接",
		emotionTags: "孤独关系",
		courseCategory: "关系连接",
		exerciseType: "关系连接",
		goals: [
			"赞美别人",
			"对别人说谢谢",
			"想起今天帮助过我的人",
			"向亲近的人表达感恩",
			"说出一个关心我的人",
			"和身边的人分享一个微笑",
			"给别人一个温暖的拥抱",
			"问问别人今天过得怎么样",
			"告诉亲近的人“我爱你”",
			"睡前拥抱亲近的人",
			"和亲近的人一起拍张照片",
			"给亲近的人打电话",
			"和亲近的人分享今天的一件事",
			"和亲近的人相处时放下手机",
			"关心一下我在意的人",
			"和家人一起做饭",
			"和家人共度高质量时光",
			"给朋友发消息",
			"和朋友出去玩 / 聚一聚",
			"给朋友发一个有趣的内容",
			"邀请朋友一起跑腿办事",
			"跟邻居打招呼",
			"帮助邻居",
			"参与本地组织的志愿活动",
			"参加社团或聚会活动",
			"照顾社区花园",
		],
	},
	{
		key: "gratitude",
		category: "Gratitude",
		zhName: "感恩",
		questionDomain: "价值目标",
		core: "培养感恩心态，提升正面情绪",
		examples: "写 3 件感恩的事、晚上回顾美好时刻、对自己说谢谢",
		detectedState: "低落、消极、缺少正向体验，需要积极情绪",
		emotionTags: "低落无力, 平静积极",
		courseCategory: "感恩练习",
		exerciseType: "感恩积极",
		goals: [
			"睡前说出一件感恩的事",
			"感谢自己撑过了今天",
			"说出对身体的一件感恩的事",
			"赞美别人",
			"说出欣赏别人的一点",
			"留意今天的一件小确幸",
			"写下一件感恩的事",
			"说出今天一件让我开心的事",
			"对别人说谢谢",
			"花片刻时间感激新鲜空气",
			"抬头看天空，欣赏它的美丽",
			"吃饭前停顿一下，感恩食物",
			"留意身边一件带来平静的事物",
			"想起今天帮助过我的人",
			"把一件日常物品当作礼物对待",
			"说出今天自己做得不错的一件小事",
			"回想一段美好的回忆",
			"停顿一下，对自己说“谢谢你”",
			"想想家务如何让我的生活更好",
			"起床前感激床的舒适",
			"向亲近的人表达感恩",
		],
	},
	{
		key: "hygiene",
		category: "Hygiene",
		zhName: "卫生 / 个人护理",
		questionDomain: "身体能量",
		core: "个人清洁和日常打理",
		examples: "刷牙、洗脸、洗澡、护肤、换衣服、剪指甲",
		detectedState: "低能量、混乱、自我照顾不足",
		emotionTags: "身体照顾, 低落无力",
		courseCategory: "基础自我照顾",
		exerciseType: "身体照顾",
		goals: [
			"刷牙",
			"洗脸",
			"洗澡",
			"用牙线清洁牙齿",
			"回家后洗手",
			"梳头",
			"穿干净衣服",
			"穿干净袜子",
			"剪指甲",
			"给皮肤涂保湿霜",
			"洗头发",
			"擦拭手机",
			"消毒键盘",
			"外出时带免洗洗手液",
			"清洗浴巾",
			"清洗床单",
			"洗衣服",
			"擦拭浴室台面",
			"清洗水杯",
			"出门前涂防晒霜",
		],
	},
	{
		key: "movement",
		category: "Movement",
		zhName: "运动",
		questionDomain: "身体能量",
		core: "身体活动、活动筋骨",
		examples: "散步 10 分钟、简单拉伸、跳舞、做瑜伽、站立一会儿",
		detectedState: "疲惫、低能量、身体僵硬，需要启动",
		emotionTags: "睡眠疲惫, 低落无力",
		courseCategory: "身体启动",
		exerciseType: "身体活动",
		goals: [
			"做一次伸展休息",
			"出门一次",
			"散步 5 分钟",
			"做 3 分钟我喜欢的运动",
			"跟着喜欢的歌跳舞",
			"走楼梯",
			"在小区/附近走走",
			"在大自然中散步",
			"用晨间伸展开始一天",
			"用放松伸展结束一天",
			"做 10 次肩部绕圈",
			"保持平板支撑 10 秒",
			"做 5 次开合跳",
			"做 5 次深蹲",
			"做 5 次墙壁俯卧撑",
			"做 5 次仰卧起坐",
			"做 10 次弓步",
			"打电话时来回踱步",
			"看电视时站起来一次",
			"饭后短距离散步",
		],
	},
	{
		key: "nutrition",
		category: "Nutrition",
		zhName: "营养",
		questionDomain: "身体能量",
		core: "饮食和身体滋养",
		examples: "喝水、多吃蔬菜、慢慢咀嚼、吃一顿均衡餐、补充维生素",
		detectedState: "疲惫、身体照顾不足、饮食失衡",
		emotionTags: "身体照顾, 睡眠疲惫",
		courseCategory: "身体滋养",
		exerciseType: "身体照顾",
		goals: [
			"喝水",
			"吃一顿健康餐",
			"避免含糖饮料",
			"吃健康零食（如完整水果或坚果）",
			"装满我的水杯",
			"全天随身带着水杯",
			"用小盘子控制份量",
			"去超市买健康零食",
			"在餐食中加入蔬菜",
			"每吃一口就放下叉子",
			"吃饭时不看手机或电视",
			"今晚不喝那杯饮料",
			"喝一杯茶",
			"每餐喝一杯水",
			"细细品味每一口食物",
			"为今天准备健康零食",
			"提前规划明天的餐食",
			"尝试一个新食谱",
			"自己做一顿饭",
			"在餐食中加入全谷物",
		],
	},
	{
		key: "productivity",
		category: "Productivity",
		zhName: "生产力",
		questionDomain: "行动动力",
		core: "专注、任务完成、日常效率",
		examples: "完成一项工作、番茄钟专注 25 分钟、规划一天、整理桌面",
		detectedState: "拖延、混乱、低效率、缺少掌控感",
		emotionTags: "拖延失控",
		courseCategory: "任务启动",
		exerciseType: "任务管理",
		goals: [
			"写下明天的目标",
			"关闭通知 1 小时",
			"从待办事项中勾掉一项",
			"按时完成工作",
			"为任务设定截止时间",
			"早上写下今天的目标",
			"把任务拆分成更小的步骤",
			"用专注计时器进行深度工作",
			"从最重要的任务开始",
			"在一天结束时说出自己完成的一件事",
			"听专注力播放列表",
			"写下任务的第一步",
			"在午饭前完成一项任务",
			"设置特定时间查看消息",
			"关闭没用的浏览器标签页",
			"把一件乱放的物品归位",
			"清理办公桌",
			"花 5 分钟收拾家里",
			"倒垃圾",
			"给房间吸尘",
		],
	},
	{
		key: "self-kindness",
		category: "Self-Kindness",
		zhName: "自我善待",
		questionDomain: "情绪状态",
		core: "自爱、自我慈悲、温柔对待自己",
		examples: "对自己说肯定的话、休息不自责、做一件让自己开心的事、原谅自己",
		detectedState: "自责、内耗、自我批评，需要自我接纳",
		emotionTags: "自责内耗",
		courseCategory: "自我接纳",
		exerciseType: "自我慈悲",
		goals: [
			"做一件让自己开心的事",
			"说出今天自己做得不错的一件小事",
			"允许自己休息",
			"给自己一个拥抱",
			"像对待朋友一样对自己说一句温暖的话",
			"感谢自己撑过了今天",
			"说出对身体的一件感恩的事",
			"读一条简单的自我肯定语",
			"说出自己喜欢自己的一点",
			"说出今天的一件小成就",
			"给自己一个小奖励",
			"用一句自我肯定开始一天",
			"对着镜子对自己微笑",
			"接受自己正在进步中",
			"说出自己正在培养的一项品质",
			"拥抱自己的不完美，它正是独特之处",
			"允许自己感受情绪，不加评判",
			"说出“我接受现在的自己”",
			"把“我应该”换成“我选择”",
			"感到 overwhelmed 时告诉自己“我只需要尽力就好”",
			"停顿一下，对自己说“谢谢你”",
		],
	},
	{
		key: "sleep",
		category: "Sleep",
		zhName: "睡眠",
		questionDomain: "睡眠压力",
		core: "改善睡眠质量和作息",
		examples: "早点上床、睡前不看手机、设置固定起床时间、睡前放松仪式",
		detectedState: "睡眠差、疲惫、焦虑、作息混乱",
		emotionTags: "睡眠疲惫, 焦虑压力",
		courseCategory: "睡眠修复",
		exerciseType: "睡眠修复",
		goals: [
			"早上晒 10 分钟阳光",
			"每晚固定时间上床睡觉",
			"午饭后避免摄入咖啡因",
			"睡前说出一件感恩的事",
			"睡前避免吃太油腻的食物",
			"把所有想法和担忧写下来",
			"睡前关闭所有通知",
			"睡前做睡眠呼吸练习放松",
			"睡前阅读放松的内容",
			"晚饭后泡一杯洋甘菊茶",
			"睡前 30 分钟调暗灯光",
			"睡前听舒缓的音乐",
			"睡前洗个热水澡",
			"睡前限制刷社交媒体",
			"拉上所有窗帘保持黑暗",
			"整理床铺",
			"睡前听轻柔的音乐",
			"避免午睡超过 30 分钟",
			"早上第一件事就是拉开窗帘",
			"固定时间起床",
			"睡前 3 小时吃完最后一餐",
			"今晚不喝那杯饮料",
		],
	},
];

const mindDocQuestions = [
	{
		key: "rumination",
		question: "你是否常常会有那种看似在解决问题但实际上却永远无法得出答案的循环性思维？",
		questionDomain: "情绪状态",
		testedState: "反刍思维、内耗、焦虑性担忧",
		detectedState: "内耗、焦虑、反复思考",
		emotionTags: "自责内耗, 焦虑压力",
		courseCategory: "停止反复思考",
		exerciseType: "认知梳理",
	},
	{
		key: "action",
		question: "你是否经常采取行动去实现某事？你为工作或家庭设定的目标有哪些？",
		questionDomain: "行动动力",
		testedState: "行动力、目标感、行为启动能力",
		detectedState: "拖延、低动力、缺少目标",
		emotionTags: "拖延失控, 低落无力",
		courseCategory: "行动课程",
		exerciseType: "行动启动",
	},
	{
		key: "sleep",
		question: "你是否有充足的睡眠？",
		questionDomain: "睡眠压力",
		testedState: "睡眠质量与恢复感",
		detectedState: "睡眠差、疲惫",
		emotionTags: "睡眠疲惫",
		courseCategory: "睡眠课程",
		exerciseType: "睡眠修复",
	},
	{
		key: "balance",
		question: "你能否维持工作生活平衡？",
		questionDomain: "睡眠压力",
		testedState: "压力负荷、恢复边界、生活掌控感",
		detectedState: "压力、疲惫、失衡",
		emotionTags: "焦虑压力, 睡眠疲惫",
		courseCategory: "压力管理",
		exerciseType: "任务管理",
	},
	{
		key: "conflict",
		question: "你是否经常和其他人发生冲突或争吵？",
		questionDomain: "关系场景",
		testedState: "关系冲突、人际压力",
		detectedState: "愤怒、烦躁、关系困扰",
		emotionTags: "烦躁愤怒, 孤独关系",
		courseCategory: "人际关系",
		exerciseType: "关系连接",
	},
	{
		key: "relationship",
		question: "人际关系对你来说困难吗？",
		questionDomain: "关系场景",
		testedState: "关系连接、社交压力",
		detectedState: "孤独、社交压力、关系疏离",
		emotionTags: "孤独关系",
		courseCategory: "积极塑造人际关系",
		exerciseType: "关系连接",
	},
	{
		key: "energy",
		question: "你是否经常感觉低能量？",
		questionDomain: "身体能量",
		testedState: "身体能量、疲劳感、低动力",
		detectedState: "低能量、疲惫、低动力",
		emotionTags: "睡眠疲惫, 低落无力",
		courseCategory: "行动课程",
		exerciseType: "行动启动",
	},
	{
		key: "anxiety-confidence",
		question: "你是否经常感觉紧张、焦虑，或在面对新问题时缺乏自信？",
		questionDomain: "情绪状态",
		testedState: "焦虑、紧张、自信水平",
		detectedState: "焦虑、紧张、缺乏自信",
		emotionTags: "焦虑压力, 自责内耗",
		courseCategory: "广泛性焦虑 / 自信课程",
		exerciseType: "呼吸放松",
	},
	{
		key: "body-health",
		question: "你对自己的身体健康状态满意吗？",
		questionDomain: "身体能量",
		testedState: "身体健康满意度、自我照顾",
		detectedState: "身体担忧、自我照顾不足",
		emotionTags: "身体照顾",
		courseCategory: "关注身体健康",
		exerciseType: "身体照顾",
	},
	{
		key: "self-critical",
		question: "你对自己是否很挑剔？",
		questionDomain: "情绪状态",
		testedState: "自我批评、自责、自我接纳",
		detectedState: "自责、自我批评、内耗",
		emotionTags: "自责内耗",
		courseCategory: "自信 / 自我接纳",
		exerciseType: "自我慈悲",
	},
	{
		key: "current-mood",
		question: "你现在的心情如何？",
		questionDomain: "情绪状态",
		testedState: "当下心情",
		detectedState: "低落、焦虑、平静、开心",
		emotionTags: "低落无力, 焦虑压力, 平静积极",
		courseCategory: "情绪课程",
		exerciseType: "情绪觉察",
	},
	{
		key: "panic-attack",
		question: "你是否有过一次或者多次的恐慌发作？",
		questionDomain: "情绪状态",
		testedState: "恐慌发作体验",
		detectedState: "恐慌、焦虑",
		emotionTags: "焦虑压力",
		courseCategory: "焦虑课程",
		exerciseType: "呼吸放松",
	},
	{
		key: "panic-avoidance",
		question:
			"你最近是否害怕出现恐慌发作而改变了自己的行为，或者至少连续几周避免了某些特定的情况？",
		questionDomain: "情绪状态",
		testedState: "恐慌相关回避行为",
		detectedState: "恐慌、回避、焦虑",
		emotionTags: "焦虑压力, 拖延失控",
		courseCategory: "焦虑课程",
		exerciseType: "行动启动",
	},
	{
		key: "specific-fear",
		question:
			"你是否有一些特别让你感到极度恐惧的事物或者情景，比如看到血、昆虫或者动物，或者坐飞机？",
		questionDomain: "情绪状态",
		testedState: "特定恐惧触发物",
		detectedState: "恐惧、回避",
		emotionTags: "焦虑压力",
		courseCategory: "焦虑课程",
		exerciseType: "呼吸放松",
	},
	{
		key: "social-anxiety",
		question: "你是否在社交场合中感到极度不适，还是害怕在别人面前说话、进食或书写？",
		questionDomain: "关系场景",
		testedState: "社交焦虑、被评价恐惧",
		detectedState: "社交压力、焦虑、回避",
		emotionTags: "焦虑压力, 孤独关系",
		courseCategory: "人际关系 / 焦虑课程",
		exerciseType: "关系连接",
	},
	{
		key: "depressed-mood",
		question: "在过去的两周里，你是否感到情绪低落、沮丧或者绝望？",
		questionDomain: "情绪状态",
		testedState: "近两周低落、沮丧、绝望感",
		detectedState: "低落、绝望、抑郁风险",
		emotionTags: "低落无力",
		courseCategory: "情绪课程",
		exerciseType: "行动启动",
	},
];

const headspaceQuestions = [
	{
		key: "unexpected-events",
		question: "在过去一个月里，你是否经常为意外事件心烦意乱？",
		testedState: "压力事件反应",
		detectedState: "压力、烦躁、失控感",
		emotionTags: "焦虑压力, 烦躁愤怒",
	},
	{
		key: "control-important-things",
		question: "在过去一个月里，你是否觉得无法控制生活中的重要事项？",
		testedState: "不可控感、生活压力",
		detectedState: "压力、失控感、焦虑",
		emotionTags: "焦虑压力, 拖延失控",
	},
	{
		key: "nervous-stressed",
		question: "在过去一个月里，你是否经常感到紧张和有压力？",
		testedState: "紧张感、主观压力",
		detectedState: "压力、紧张、焦虑",
		emotionTags: "焦虑压力",
	},
	{
		key: "confidence-problems",
		question: "在过去一个月里，你是否觉得自己处理个人问题时信心满满？",
		testedState: "应对信心、压力下的掌控感",
		detectedState: "缺乏自信、压力、低掌控感",
		emotionTags: "自责内耗, 焦虑压力, 拖延失控",
	},
	{
		key: "things-going-way",
		question: "在过去一个月里，你是否觉得事情正在向自己期待的方向发展？",
		testedState: "生活顺利感、掌控感",
		detectedState: "压力、低掌控感、迷茫",
		emotionTags: "焦虑压力, 拖延失控",
	},
];

const sampleRecords: QuestionRecord[] = [
	...scientificReferenceRecords,
	...finchGoalCategories.flatMap((category) =>
		category.goals.map((goal, index) =>
			createRecord({
				id: `finch-${category.key}-${index + 1}`,
				product: "Finch",
				region: "国外产品",
				productType: "自我照顾 + 微习惯打卡 + 宠物养成",
				tags: `Finch, 目标, ${category.category}, ${category.zhName}, 宠物养成`,
				question: `添加目标：${category.category}（${category.zhName}）`,
				answerFormat: "多选",
				intakeMethod: "场景选择",
				dailyFit: "触发时询问",
				questionDomain: category.questionDomain,
				testedState: category.core,
				detectedState: category.detectedState,
				emotionTags: category.emotionTags,
				courseCategory: category.courseCategory,
				training: goal,
				exerciseType: category.exerciseType,
				trainingTheory: finchTrainingTheory,
				dataPurpose: "记录用户选择的具体训练，用于反推状态和训练偏好",
				productCore: finchProductCore,
				reason: "Finch 通过用户主动选择目标类别理解当前需求，不是临床量表",
				source: "Finch 添加目标问题梳理.md",
				notes: `分类核心：${category.core}。常见目标例子：${category.examples}`,
			}),
		),
	),
	createRecord({
		id: "linjian-core-question",
		product: "林间疗愈室",
		region: "中文产品",
		productType: "AI 情绪陪伴 + 轻训练 + 成长计划",
		tags: "AI对话, 情绪陪伴, 轻训练, 21天计划",
		question: "这段时间，最让你心累的困扰是？",
		answerFormat: "对话",
		intakeMethod: "AI对话",
		dailyFit: "触发时询问",
		questionDomain: "情绪状态",
		testedState: "自由表达里的情绪、困扰主题、问题模式",
		detectedState: "低落、迷茫、内耗、拖延、焦虑、成长困境",
		emotionTags: "低落无力, 自责内耗, 拖延失控, 焦虑压力",
		courseCategory: "情绪觉察, 认知梳理, 行动启动",
		training: "AI 陪聊和梳理，进入 21 天计划或早晚音频",
		exerciseType: "AI陪伴",
		trainingTheory: "支持性陪伴, 情绪标记, 认知梳理, 自我关怀, 行为激活, 习惯训练",
		dataPurpose: "从自由输入识别长期困扰和可训练方向",
		productCore: "用户表达困扰，AI 陪聊和梳理，再给工具或计划并记录长期变化",
		reason: "自由输入能保留真实语境，适合做 AI 情绪识别和后续追问",
	}),
	createRecord({
		id: "daylio-mood-activity",
		product: "Daylio",
		region: "国外产品",
		productType: "心情日记 + 习惯追踪",
		tags: "心情, 活动, 习惯, 目标, 数据统计",
		question: "今天心情如何？这一阵子都在忙些什么？",
		answerFormat: "多选",
		intakeMethod: "行为记录",
		dailyFit: "每日必问",
		questionDomain: "情绪状态",
		testedState: "心情、活动、睡眠、目标之间的关系",
		detectedState: "开心、低落、压力、平静、习惯变化",
		emotionTags: "平静积极, 低落无力, 焦虑压力",
		courseCategory: "行为激活, 习惯建立",
		training: "心情和活动记录",
		exerciseType: "记录反思",
		trainingTheory: "自我监测, 量化自我, 行为-情绪关联",
		dataPurpose: "长期积累个人模式，统计什么活动和心情有关",
		productCore: "价值不在诊断，而在长期记录心情、活动和目标并看到趋势",
		reason: "情绪和活动一起记录，能反推用户自己的有效行为",
	}),
	createRecord({
		id: "how-we-feel-check-in",
		product: "How We Feel",
		region: "国外产品",
		productType: "情绪识别",
		tags: "情绪词, 情绪粒度, 睡眠, 运动, 朋友分享",
		question: "让用户从情绪矩阵和情绪词里找到准确感受，并结合睡眠、运动等标签。",
		answerFormat: "多选",
		intakeMethod: "单个问题",
		dailyFit: "每日必问",
		questionDomain: "情绪状态",
		testedState: "细分情绪、身体状态、生活因素",
		detectedState: "压力、焦虑、紧张、疲惫、平静、愉悦",
		emotionTags: "焦虑压力, 睡眠疲惫, 平静积极",
		courseCategory: "情绪觉察, 情绪调节",
		training: "1 分钟情绪调节策略",
		exerciseType: "情绪觉察",
		trainingTheory: "情绪粒度, 情绪命名, 情绪智力, 情绪调节",
		dataPurpose: "提高情绪分类精度，发现情绪和生活因素关系",
		productCore: "让用户更准确命名情绪，再通过历史趋势、健康数据关联和朋友分享形成反馈",
		reason: "细分情绪词能提高觉察精度，标签用于发现影响情绪的模式",
	}),
	...mindDocQuestions.map((item) =>
		createRecord({
			id: `minddoc-${item.key}`,
			product: "MindDoc",
			region: "国外产品",
			productType: "心理测评/课程",
			tags: "周期性评估, 心情追踪, 课程推荐, 会员课程",
			question: item.question,
			answerFormat: "单选",
			intakeMethod: "问卷/量表",
			dailyFit: "每周追踪",
			questionDomain: item.questionDomain,
			testedState: item.testedState,
			detectedState: item.detectedState,
			emotionTags: item.emotionTags,
			courseCategory: item.courseCategory,
			training: item.courseCategory,
			exerciseType: item.exerciseType,
			trainingTheory: "提问评估, 心情追踪, 课程推荐, 心理教育",
			dataPurpose: "通过周期性问题观察心理状态，再推荐课程",
			productCore: "用户记录心情、回答问题、追踪状态，并通过课程理解自己的心理状态",
			reason: "这类问题覆盖情绪、行动、睡眠、关系、自我评价和身体健康，适合做周期性状态评估",
		}),
	),
	createRecord({
		id: "meditation-planet-goal",
		product: "冥想星球",
		region: "中文产品",
		productType: "正念冥想",
		tags: "正念, 睡眠, 减压, 情绪, 专注",
		question: "你想改善什么？改善睡眠、减压放松、调整情绪、提高专注、自我成长、其他。",
		answerFormat: "单选",
		intakeMethod: "场景选择",
		dailyFit: "入门筛查",
		questionDomain: "情绪状态",
		testedState: "用户想改善的身心场景",
		detectedState: "睡眠差、压力、焦虑、专注困难、想成长",
		emotionTags: "睡眠疲惫, 焦虑压力, 拖延失控",
		courseCategory: "正念冥想, 睡眠修复, 专注训练",
		training: "选择改善目标后推荐冥想任务",
		exerciseType: "正念冥想",
		trainingTheory: "正念减压, 呼吸训练, 放松训练, 习惯提醒",
		dataPurpose: "用场景入口快速匹配课程方向",
		productCore: "让用户先选身心改善目标，再进入对应冥想训练",
		reason: "场景选择比量表更轻，适合把用户直接带到课程",
	}),
	createRecord({
		id: "meditation-planet-emotion",
		product: "冥想星球",
		region: "中文产品",
		productType: "正念冥想",
		tags: "正念, 情绪管理, 焦虑, 低落, 烦躁",
		question: "你的具体情绪是什么？焦虑不安、情绪低落、容易烦躁。",
		answerFormat: "单选",
		intakeMethod: "场景选择",
		dailyFit: "入门筛查",
		questionDomain: "情绪状态",
		testedState: "情绪调节的具体入口",
		detectedState: "焦虑、低落、烦躁",
		emotionTags: "焦虑压力, 低落无力, 烦躁愤怒",
		courseCategory: "情绪管理",
		training: "根据情绪入口推荐冥想任务和练习提醒",
		exerciseType: "呼吸放松",
		trainingTheory: "正念减压, 情绪觉察, 放松训练",
		dataPurpose: "细分情绪入口，用于匹配更具体的冥想课程",
		productCore: "把情绪问题转成可执行的冥想练习",
		reason: "焦虑、低落、烦躁是用户容易自我识别的高频情绪入口",
	}),
	...headspaceQuestions.map((item) =>
		createRecord({
			id: `headspace-${item.key}`,
			product: "Headspace",
			region: "国外产品",
			productType: "压力管理 + 冥想课程",
			tags: "压力测试, 冥想, 睡眠, 专注, 正念",
			question: item.question,
			answerFormat: "打分",
			intakeMethod: "问卷/量表",
			dailyFit: "入门筛查",
			questionDomain: "睡眠压力",
			testedState: item.testedState,
			detectedState: item.detectedState,
			emotionTags: item.emotionTags,
			courseCategory: "压力管理, 冥想入门, 日常舒压",
			training: "压力管理冥想或日常舒压课程",
			exerciseType: "正念冥想",
			trainingTheory: "压力感知评估, 正念减压, 冥想训练",
			dataPurpose: "用压力感知问题判断压力水平，再推荐冥想和放松课程",
			productCore: "通过压力测试和场景课程，把压力管理转成可听可练的内容",
			reason: "这些题围绕过去一个月的不可控感、紧张感、应对信心和掌控感，适合判断主观压力",
		}),
	),
	createRecord({
		id: "yixinli-paid-tests",
		product: "壹心理",
		region: "中文产品",
		productType: "心理测评平台",
		tags: "测评, 会员, 心理健康, 职场, 人际, 亲子",
		question:
			"收费/会员测评入口：抑郁测评、理想恋人测评、心理年龄测试、暗黑人格魅力测试、性格类型测试、爱情检测、心理健康、职场能力、人际关系测评、亲子测试。",
		answerFormat: "量表",
		intakeMethod: "问卷/量表",
		dailyFit: "入门筛查",
		questionDomain: "情绪状态",
		testedState: "心理健康、人格、关系、职场、亲子等主题",
		detectedState: "抑郁风险、关系困扰、职场压力、人格倾向",
		emotionTags: "低落无力, 孤独关系, 焦虑压力",
		courseCategory: "心理测评, 付费报告, 课程转化",
		training: "测评报告和课程转化",
		exerciseType: "量表评估",
		trainingTheory: "心理测评, 主题化报告, 付费转化",
		dataPurpose: "用测评主题吸引用户自我探索，再转化为报告或课程",
		productCore: "把泛心理问题包装成测评和报告，覆盖心理健康、人际、职场、亲密关系等主题",
		reason: "测评题适合做入门筛查和商业转化，但需要区分娱乐测评和严肃心理量表",
	}),
];

const mergeScientificReferenceRecords = (records: QuestionRecord[]) => {
	const existingIds = new Set(records.map((record) => record.id));
	const missingReferences = scientificReferenceRecords.filter(
		(record) => !existingIds.has(record.id),
	);
	return missingReferences.length > 0 ? [...missingReferences, ...records] : records;
};

const createId = () => {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
		return crypto.randomUUID();
	}
	return `record-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const fieldClass =
	"min-h-11 w-full rounded-xl border border-input bg-background/90 px-3 py-2 text-sm shadow-sm transition-[border-color,box-shadow] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const tableHeadClass =
	"whitespace-nowrap border-b border-border bg-muted/70 px-3 py-3 text-left text-xs font-semibold text-muted-foreground";

const tableCellClass = "min-w-[180px] align-top border-b border-border/70 px-3 py-3 text-sm";
const tagSeparators = /[,，、;；/]+/;

const splitTags = (value: string) =>
	value
		.split(tagSeparators)
		.map((item) => item.trim())
		.filter(Boolean);

const normalizeTags = (value: string) => splitTags(value).join(", ");

function loadRecords() {
	if (typeof window === "undefined") {
		return sampleRecords;
	}
	const stored = window.localStorage.getItem(storageKey);
	if (!stored) {
		return sampleRecords;
	}
	try {
		const parsed = JSON.parse(stored) as QuestionRecord[];
		return parsed.length > 0 ? mergeScientificReferenceRecords(parsed) : sampleRecords;
	} catch {
		return sampleRecords;
	}
}

const escapeCsv = (value: string) => `"${value.replaceAll('"', '""')}"`;

const downloadFile = (fileName: string, content: string, mimeType: string) => {
	const blob = new Blob([content], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	link.click();
	URL.revokeObjectURL(url);
};

const toCsv = (records: QuestionRecord[]) => {
	const headers: Array<keyof QuestionRecord> = [
		"product",
		"region",
		"productType",
		"tags",
		"question",
		"answerFormat",
		"intakeMethod",
		"dailyFit",
		"questionDomain",
		"testedState",
		"detectedState",
		"emotionTags",
		"courseCategory",
		"training",
		"exerciseType",
		"trainingTheory",
		"dataPurpose",
		"productCore",
		"reason",
		"evidenceLevel",
		"source",
		"notes",
	];
	const titleMap: Record<keyof QuestionRecord, string> = {
		id: "ID",
		product: "产品",
		region: "地区",
		productType: "产品类型",
		tags: "标签",
		question: "原题/入口",
		answerFormat: "回答形式",
		intakeMethod: "采集方式",
		dailyFit: "每日使用位置",
		questionDomain: "问题门类",
		testedState: "问题在测什么状态",
		detectedState: "识别出的状态",
		emotionTags: "处理状态",
		courseCategory: "课程分类",
		training: "识别后训练",
		exerciseType: "训练分类",
		trainingTheory: "训练理论",
		dataPurpose: "数据用途",
		productCore: "产品核心判断",
		reason: "为什么用这套题",
		evidenceLevel: "证据等级",
		source: "来源",
		notes: "备注",
		createdAt: "创建时间",
	};
	const rows = [
		headers.map((header) => escapeCsv(titleMap[header])).join(","),
		...records.map((record) => headers.map((header) => escapeCsv(record[header])).join(",")),
	];
	return rows.join("\n");
};

export const Home: React.FC = () => {
	const [records, setRecords] = useState<QuestionRecord[]>(loadRecords);
	const [draft, setDraft] = useState<DraftRecord>(emptyDraft);
	const [query, setQuery] = useState("");
	const [activeEmotion, setActiveEmotion] = useState<string | null>(null);
	const [activeTraining, setActiveTraining] = useState<string | null>(null);

	useEffect(() => {
		window.localStorage.setItem(storageKey, JSON.stringify(records));
	}, [records]);

	const filteredRecords = useMemo(() => {
		const normalizedQuery = query.trim().toLowerCase();
		return records.filter((record) => {
			const text = [
				record.product,
				record.productType,
				record.tags,
				record.question,
				record.dailyFit,
				record.questionDomain,
				record.testedState,
				record.detectedState,
				record.emotionTags,
				record.courseCategory,
				record.training,
				record.exerciseType,
				record.trainingTheory,
				record.dataPurpose,
				record.productCore,
				record.reason,
				record.source,
			]
				.join(" ")
				.toLowerCase();
			const emotionTags = splitTags(record.emotionTags);
			const trainingTags = splitTags(record.exerciseType);
			const matchesQuery = normalizedQuery === "" || text.includes(normalizedQuery);
			const matchesEmotion = activeEmotion === null || emotionTags.includes(activeEmotion);
			const matchesTraining = activeTraining === null || trainingTags.includes(activeTraining);
			return matchesQuery && matchesEmotion && matchesTraining;
		});
	}, [activeEmotion, activeTraining, query, records]);

	const emotionCounts = useMemo(() => {
		const counts = records.reduce<Record<string, number>>((acc, record) => {
			for (const tag of new Set(splitTags(record.emotionTags))) {
				acc[tag] = (acc[tag] ?? 0) + 1;
			}
			return acc;
		}, {});
		return Object.entries(counts).sort((left, right) => right[1] - left[1]);
	}, [records]);

	const trainingCounts = useMemo(() => {
		const counts = records.reduce<Record<string, number>>((acc, record) => {
			for (const tag of new Set(splitTags(record.exerciseType))) {
				acc[tag] = (acc[tag] ?? 0) + 1;
			}
			return acc;
		}, {});
		return Object.entries(counts).sort((left, right) => right[1] - left[1]);
	}, [records]);

	const canSave = draft.product.trim().length > 0 && draft.question.trim().length > 0;

	const updateDraft = <K extends keyof DraftRecord>(key: K, value: DraftRecord[K]) => {
		setDraft((current) => ({ ...current, [key]: value }));
	};

	const addRecord = () => {
		if (!canSave) {
			return;
		}
		setRecords((current) => [
			{
				...draft,
				emotionTags: normalizeTags(draft.emotionTags),
				exerciseType: normalizeTags(draft.exerciseType),
				id: createId(),
				createdAt: new Date().toISOString(),
			},
			...current,
		]);
		setDraft(emptyDraft);
	};

	const duplicateRecord = (record: QuestionRecord) => {
		const { id: _id, createdAt: _createdAt, ...copy } = record;
		setDraft(copy);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const deleteRecord = (id: string) => {
		setRecords((current) => current.filter((record) => record.id !== id));
	};

	const exportJson = () => {
		downloadFile(
			"psychology.json",
			JSON.stringify(records, null, 2),
			"application/json;charset=utf-8",
		);
	};

	const exportCsv = () => {
		downloadFile("psychology.csv", toCsv(records), "text/csv;charset=utf-8");
	};

	const resetSamples = () => {
		setRecords(sampleRecords);
		setQuery("");
		setActiveEmotion(null);
		setActiveTraining(null);
	};

	return (
		<div className="space-y-6">
			<section className="flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
				<div className="space-y-3">
					<Badge variant="outline" className="w-fit">
						题库调研
					</Badge>
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-foreground">
							竞品问题采集表
						</h1>
						<p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
							测试各个 App
							时，把原题、用户状态和对应训练先录下来；PHQ/GAD、WHO-5、CBT-I、行为激活等科学参考会自动补进题库，作为更高权重的题目来源。
						</p>
					</div>
				</div>

				<div className="flex flex-wrap gap-2">
					<Badge variant="secondary" className="w-fit">
						已录 {records.length} 条
					</Badge>
					<Badge variant="outline" className="w-fit">
						科学参考 {scientificReferenceRecords.length} 条
					</Badge>
				</div>
			</section>

			<section className="rounded-lg border border-border bg-card p-5 shadow-sm">
				<div className="mb-5">
					<h2 className="text-lg font-semibold text-foreground">录一条题</h2>
					<p className="mt-1 text-sm text-muted-foreground">先存题，状态和训练能写多少写多少。</p>
				</div>

				<div className="space-y-4">
					<div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px_180px]">
						<Field label="产品">
							<Input
								value={draft.product}
								onChange={(event) => updateDraft("product", event.target.value)}
								placeholder="Finch / Wysa / 林间聊愈室"
							/>
						</Field>
						<Field label="来源">
							<Input
								value={draft.source}
								onChange={(event) => updateDraft("source", event.target.value)}
								placeholder="截图 01"
							/>
						</Field>
						<Field label="形式">
							<Select
								value={draft.intakeMethod}
								onChange={(event) =>
									updateDraft("intakeMethod", event.target.value as IntakeMethod)
								}
							>
								{intakeMethods.map((method) => (
									<option key={method} value={method}>
										{method}
									</option>
								))}
							</Select>
						</Field>
					</div>

					<Field label="原题">
						<Textarea
							value={draft.question}
							onChange={(event) => updateDraft("question", event.target.value)}
							placeholder="把 App 里的问题、入口文案或量表题目贴进来"
							className="min-h-28"
						/>
					</Field>

					<div className="grid gap-4 md:grid-cols-2">
						<Field label="匹配状态">
							<Input
								value={draft.emotionTags}
								onChange={(event) => updateDraft("emotionTags", event.target.value)}
								placeholder="焦虑压力 / 低落无力 / 睡眠疲惫"
							/>
						</Field>
						<Field label="训练分类">
							<Input
								value={draft.exerciseType}
								onChange={(event) => updateDraft("exerciseType", event.target.value)}
								placeholder="行动启动 / 呼吸放松 / 感恩积极"
							/>
						</Field>
					</div>

					<details className="rounded-lg border border-border bg-background/70 p-4">
						<summary className="cursor-pointer text-sm font-semibold text-foreground">
							更多字段
						</summary>
						<div className="mt-4 grid gap-4 md:grid-cols-2">
							<Field label="测什么">
								<Select
									value={draft.questionDomain}
									onChange={(event) => updateDraft("questionDomain", event.target.value)}
								>
									<option value="">未分类</option>
									{questionDomains.map((domain) => (
										<option key={domain} value={domain}>
											{domain}
										</option>
									))}
								</Select>
							</Field>
							<Field label="状态判断">
								<Input
									value={draft.testedState}
									onChange={(event) => updateDraft("testedState", event.target.value)}
									placeholder="压力强度 / 睡眠质量 / 行动动力"
								/>
							</Field>
							<Field label="训练内容">
								<Input
									value={draft.training}
									onChange={(event) => updateDraft("training", event.target.value)}
									placeholder="3分钟呼吸 / 睡前音频 / 目标任务"
								/>
							</Field>
							<Field label="产品类型">
								<Select
									value={draft.productType}
									onChange={(event) => updateDraft("productType", event.target.value)}
								>
									<option value="">未分类</option>
									{productTypes.map((type) => (
										<option key={type} value={type}>
											{type}
										</option>
									))}
								</Select>
							</Field>
							<Field label="地区">
								<Select
									value={draft.region}
									onChange={(event) => updateDraft("region", event.target.value as Region)}
								>
									{regions.map((region) => (
										<option key={region} value={region}>
											{region}
										</option>
									))}
								</Select>
							</Field>
							<Field label="证据等级">
								<Select
									value={draft.evidenceLevel}
									onChange={(event) =>
										updateDraft("evidenceLevel", event.target.value as EvidenceLevel)
									}
								>
									{evidenceLevels.map((level) => (
										<option key={level} value={level}>
											{level}
										</option>
									))}
								</Select>
							</Field>
							<Field label="备注" wide>
								<Textarea
									value={draft.notes}
									onChange={(event) => updateDraft("notes", event.target.value)}
									placeholder="截图、疑问、可借鉴点"
								/>
							</Field>
						</div>
					</details>
				</div>

				<div className="mt-5 flex flex-col gap-3 sm:flex-row">
					<Button onClick={addRecord} disabled={!canSave}>
						<Plus />
						保存记录
					</Button>
					<Button variant="outline" onClick={() => setDraft(emptyDraft)}>
						<RotateCcw />
						清空
					</Button>
				</div>
			</section>

			<section className="rounded-lg border border-border bg-card shadow-sm">
				<div className="flex flex-col gap-4 border-b border-border p-5 xl:flex-row xl:items-center xl:justify-between">
					<div className="flex flex-wrap items-center gap-3">
						<h2 className="text-lg font-semibold text-foreground">题库</h2>
						<Badge variant="secondary">{filteredRecords.length} 条</Badge>
						{activeEmotion ? (
							<Button variant="outline" size="sm" onClick={() => setActiveEmotion(null)}>
								状态：{activeEmotion}
							</Button>
						) : null}
						{activeTraining ? (
							<Button variant="outline" size="sm" onClick={() => setActiveTraining(null)}>
								训练：{activeTraining}
							</Button>
						) : null}
					</div>
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<div className="relative sm:w-72">
							<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								placeholder="搜问题、状态、训练"
								className="pl-10"
							/>
						</div>
						<div className="flex gap-2">
							<Button variant="outline" onClick={exportCsv}>
								<Download />
								CSV
							</Button>
							<Button variant="outline" onClick={exportJson}>
								<FileJson />
								JSON
							</Button>
							<Button variant="outline" onClick={resetSamples}>
								<RotateCcw />
								重置
							</Button>
						</div>
					</div>
				</div>

				<div className="space-y-4 border-b border-border p-5">
					<div className="flex flex-wrap items-center gap-2">
						<span className="mr-1 text-sm font-medium text-muted-foreground">状态筛选</span>
						<button
							type="button"
							onClick={() => setActiveEmotion(null)}
							className={cn(
								"rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
								activeEmotion === null
									? "border-primary bg-primary text-primary-foreground"
									: "border-border bg-background text-foreground hover:bg-muted",
							)}
						>
							全部
						</button>
						{emotionCounts.map(([emotion, count]) => {
							const active = activeEmotion === emotion;
							return (
								<button
									key={emotion}
									type="button"
									onClick={() => setActiveEmotion(active ? null : emotion)}
									className={cn(
										"rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
										active
											? "border-primary bg-primary text-primary-foreground"
											: "border-border bg-background text-foreground hover:bg-muted",
									)}
								>
									{emotion} · {count}
								</button>
							);
						})}
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<span className="mr-1 text-sm font-medium text-muted-foreground">训练筛选</span>
						<button
							type="button"
							onClick={() => setActiveTraining(null)}
							className={cn(
								"rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
								activeTraining === null
									? "border-primary bg-primary text-primary-foreground"
									: "border-border bg-background text-foreground hover:bg-muted",
							)}
						>
							全部
						</button>
						{trainingCounts.map(([training, count]) => {
							const active = activeTraining === training;
							return (
								<button
									key={training}
									type="button"
									onClick={() => setActiveTraining(active ? null : training)}
									className={cn(
										"rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
										active
											? "border-primary bg-primary text-primary-foreground"
											: "border-border bg-background text-foreground hover:bg-muted",
									)}
								>
									{training} · {count}
								</button>
							);
						})}
					</div>
				</div>

				<div className="overflow-x-auto">
					<table className="w-full border-collapse">
						<thead>
							<tr>
								<th className={tableHeadClass}>产品</th>
								<th className={tableHeadClass}>原题</th>
								<th className={tableHeadClass}>状态</th>
								<th className={tableHeadClass}>训练</th>
								<th className={tableHeadClass}>操作</th>
							</tr>
						</thead>
						<tbody>
							{filteredRecords.map((record) => (
								<tr key={record.id} className="transition-colors hover:bg-muted/35">
									<td className={cn(tableCellClass, "min-w-[150px]")}>
										<div className="space-y-2">
											<p className="font-semibold text-foreground">{record.product}</p>
											<div className="flex flex-wrap gap-1.5">
												<Badge>{record.intakeMethod}</Badge>
												<Badge variant="outline">{record.source || "未记录来源"}</Badge>
											</div>
										</div>
									</td>
									<td className={cn(tableCellClass, "min-w-[300px]")}>
										<p className="leading-6 text-foreground">{record.question}</p>
									</td>
									<td className={cn(tableCellClass, "min-w-[260px]")}>
										<TagList
											tags={splitTags(record.emotionTags)}
											empty="未标状态"
											activeTag={activeEmotion}
											onSelect={setActiveEmotion}
										/>
										<p className="mt-2 text-xs text-muted-foreground">
											{record.testedState || record.detectedState || "未写状态判断"}
										</p>
									</td>
									<td className={cn(tableCellClass, "min-w-[240px]")}>
										<TagList
											tags={splitTags(record.exerciseType)}
											empty={record.training || "未记录"}
											activeTag={activeTraining}
											onSelect={setActiveTraining}
										/>
										{record.training && splitTags(record.exerciseType).length > 0 ? (
											<p className="mt-2 leading-6 text-foreground">{record.training}</p>
										) : null}
									</td>
									<td className={cn(tableCellClass, "min-w-[150px]")}>
										<div className="flex gap-2">
											<Button
												variant="outline"
												size="icon"
												onClick={() => duplicateRecord(record)}
												aria-label="复制到录入区"
												title="复制到录入区"
											>
												<Copy />
											</Button>
											<Button
												variant="outline"
												size="icon"
												onClick={() => deleteRecord(record.id)}
												aria-label="删除记录"
												title="删除记录"
											>
												<Trash2 />
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
};

function Field({
	label,
	children,
	wide = false,
}: {
	label: string;
	children: React.ReactNode;
	wide?: boolean;
}) {
	return (
		<label className={cn("space-y-2", wide && "md:col-span-2")}>
			<span className="text-sm font-medium text-foreground">{label}</span>
			{children}
		</label>
	);
}

function TagList({
	tags,
	empty,
	activeTag = null,
	onSelect,
}: {
	tags: string[];
	empty: string;
	activeTag?: string | null;
	onSelect?: (tag: string | null) => void;
}) {
	if (tags.length === 0) {
		return <span className="text-sm text-muted-foreground">{empty}</span>;
	}
	return (
		<div className="flex flex-wrap gap-1.5">
			{tags.map((tag) => {
				const active = activeTag === tag;
				return (
					<button
						key={tag}
						type="button"
						onClick={() => onSelect?.(active ? null : tag)}
						disabled={!onSelect}
						className={cn(
							"rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
							active
								? "border-primary bg-primary text-primary-foreground"
								: "border-border bg-background text-foreground",
							onSelect ? "hover:bg-muted" : "cursor-default",
						)}
					>
						{tag}
					</button>
				);
			})}
		</div>
	);
}

function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
	return <select {...props} className={cn(fieldClass, props.className)} />;
}

function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
	return (
		<textarea
			{...props}
			rows={3}
			className={cn(fieldClass, "min-h-24 resize-y", props.className)}
		/>
	);
}
